/**
 * BTreePageParser — §93 §6 + §90 §6.6
 *
 * TypeScript port of B-tree page parsing for three-way merge.
 * Read-only: extracts cells with rowId, payload, and SHA-256 hash.
 *
 * Page type detection follows §90 §14.4 (verified against sqlite-src-3510200).
 */

// ── Page Type ───────────────────────────────────────────────────

export const enum BTreePageType {
    TableLeaf = 0x0D,
    TableInterior = 0x05,
    IndexLeaf = 0x0A,
    IndexInterior = 0x02,
    Unknown = 0x00,
}

// ── Cell ────────────────────────────────────────────────────────

export interface BTreeCell {
    rowId: bigint
    payloadSize: number
    payloadHash: Uint8Array   // SHA-256 (32 bytes)
    payload: Uint8Array
}

// ── Varint ──────────────────────────────────────────────────────

/**
 * Read a SQLite varint (1–9 bytes, big-endian with continuation bits).
 * Returns [value, newOffset].
 */
export function readVarint(data: Uint8Array, off: number): [bigint, number] {
    let result = 0n
    for (let i = 0; i < 9; i++) {
        const b = data[off + i]
        if (i === 8) {
            result = (result << 8n) | BigInt(b)
            return [result, off + 9]
        }
        result = (result << 7n) | BigInt(b & 0x7F)
        if ((b & 0x80) === 0) return [result, off + i + 1]
    }
    return [result, off + 9]
}

// ── Page Type Detection ─────────────────────────────────────────

/**
 * Get B-tree page type from the first byte of the B-tree header.
 *
 * Page 1 is special: DB header occupies bytes [0..99], so the B-tree
 * header starts at offset 100 (§90 §14.6 point 1).
 */
export function getPageType(page: Uint8Array, pgno: number): BTreePageType {
    const offset = pgno === 1 ? 100 : 0
    const byte = page[offset]
    switch (byte) {
        case 0x0D: return BTreePageType.TableLeaf
        case 0x05: return BTreePageType.TableInterior
        case 0x0A: return BTreePageType.IndexLeaf
        case 0x02: return BTreePageType.IndexInterior
        default: return BTreePageType.Unknown
    }
}

// ── Schema Change Detection ─────────────────────────────────────

/**
 * Compare schema cookie (page 1, offset 40, 4 bytes big-endian).
 * §90 §14.4: "Schema cookie: Page 1, offset 40 (4 bytes big-endian).
 *             Ändras → Mode 0."
 */
export function isSchemaChange(page1: Uint8Array, basePage1: Uint8Array): boolean {
    const view1 = new DataView(page1.buffer, page1.byteOffset)
    const viewBase = new DataView(basePage1.buffer, basePage1.byteOffset)
    return view1.getUint32(40) !== viewBase.getUint32(40)
}

// ── Cell Extraction ─────────────────────────────────────────────

/**
 * Extract cells from a table leaf page.
 *
 * Table leaf header (8 bytes):
 *   [0]      page type (0x0D)
 *   [1..2]   first free block offset
 *   [3..4]   cell count
 *   [5..6]   cell content area start
 *   [7]      fragmented free bytes
 *
 * Each cell: varint(nPayload) + varint(rowId) + payload[nPayload]
 *
 * @param page  Raw page data (pageSize bytes)
 * @param pgno  Page number (1-based, affects header offset for page 1)
 * @param pageGetter Optional function to read arbitrary pages (for overflow chain)
 * @param pageSize   Page size (required if pageGetter provided)
 */
export async function extractCells(
    page: Uint8Array, pgno: number,
    pageGetter?: (pgno: number) => Uint8Array | null,
    pageSize?: number,
): Promise<BTreeCell[]> {
    const hdrOff = pgno === 1 ? 100 : 0
    const view = new DataView(page.buffer, page.byteOffset)

    const pageType = page[hdrOff]
    if (pageType !== 0x0D) {
        throw new Error(`extractCells: not a table leaf page (type 0x${pageType.toString(16)})`)
    }

    const cellCount = view.getUint16(hdrOff + 3)
    const cellPtrStart = hdrOff + 8  // leaf: 8-byte header

    // Compute local storage thresholds (§90 §14.7)
    const usableSize = pageSize ?? page.length
    const maxLocal = usableSize - 35
    const minLocal = Math.floor(((usableSize - 12) * 32) / 255) - 23

    const cells: BTreeCell[] = []
    for (let i = 0; i < cellCount; i++) {
        const cellOff = view.getUint16(cellPtrStart + i * 2)

        const [nPayload, off2] = readVarint(page, cellOff)
        const [rowId, off3] = readVarint(page, off2)
        const P = Number(nPayload)

        let payload: Uint8Array

        if (P <= maxLocal || !pageGetter) {
            // No overflow, or no page getter — read what's on-page
            const payloadLen = Math.min(P, page.length - off3)
            payload = page.slice(off3, off3 + payloadLen)
        } else {
            // Overflow: compute local storage amount per SQLite spec
            let localBytes = minLocal + ((P - minLocal) % (usableSize - 4))
            if (localBytes > maxLocal) localBytes = minLocal

            // Read on-page payload portion
            const fullPayload = new Uint8Array(P)
            fullPayload.set(page.subarray(off3, off3 + localBytes), 0)

            // Read overflow page pointer (4 bytes after on-page payload)
            let overflowPgno = new DataView(page.buffer, page.byteOffset)
                .getUint32(off3 + localBytes)

            // Follow overflow chain
            let written = localBytes
            const overflowUsable = usableSize - 4 // first 4 bytes = next page ptr
            while (overflowPgno > 0 && written < P) {
                const ovflPage = pageGetter(overflowPgno)
                if (!ovflPage) break
                const ovflView = new DataView(ovflPage.buffer, ovflPage.byteOffset)
                const nextOvfl = ovflView.getUint32(0)
                const bytesToRead = Math.min(overflowUsable, P - written)
                fullPayload.set(ovflPage.subarray(4, 4 + bytesToRead), written)
                written += bytesToRead
                overflowPgno = nextOvfl
            }
            payload = fullPayload
        }

        const hashBuf = await crypto.subtle.digest('SHA-256', payload as BufferSource)

        cells.push({
            rowId: BigInt(rowId),
            payloadSize: P,
            payloadHash: new Uint8Array(hashBuf),
            payload,
        })
    }
    return cells
}

// ── Record Format Parser (§90 §6.3.1) ──────────────────────────

/**
 * Parse a SQLite record (row payload) into individual column values.
 *
 * Record format:
 *   varint(headerSize) + varint(serialType1) + ... + varint(serialTypeN)
 *   + value1 + value2 + ... + valueN
 *
 * Serial types:
 *   0 = NULL
 *   1 = int8
 *   2 = int16
 *   3 = int24
 *   4 = int32
 *   5 = int48
 *   6 = int64
 *   7 = float64
 *   8 = integer 0
 *   9 = integer 1
 *   N >= 12, even → blob of length (N-12)/2
 *   N >= 13, odd  → text of length (N-13)/2
 */
export function parseRecord(payload: Uint8Array): Uint8Array[] {
    const [headerSize, firstTypeOff] = readVarint(payload, 0)
    const hdrEnd = Number(headerSize)

    // Parse serial types
    const serialTypes: bigint[] = []
    let off = firstTypeOff
    while (off < hdrEnd) {
        const [st, nextOff] = readVarint(payload, off)
        serialTypes.push(st)
        off = nextOff
    }

    // Extract column values
    const columns: Uint8Array[] = []
    let dataOff = hdrEnd
    for (const st of serialTypes) {
        const n = Number(st)
        let len = 0
        if (n === 0) len = 0
        else if (n >= 1 && n <= 4) len = n
        else if (n === 5) len = 6
        else if (n === 6 || n === 7) len = 8
        else if (n === 8 || n === 9) len = 0
        else if (n >= 12 && n % 2 === 0) len = (n - 12) / 2
        else if (n >= 13 && n % 2 === 1) len = (n - 13) / 2

        columns.push(payload.slice(dataOff, dataOff + len))
        dataOff += len
    }
    return columns
}

// ── Varint Writer ───────────────────────────────────────────────

/**
 * Encode a value as a SQLite varint (1–9 bytes).
 * Inverse of readVarint.
 */
export function writeVarint(value: bigint): Uint8Array {
    if (value < 0n) throw new Error('writeVarint: negative value')

    // Special case: values < 128 are 1 byte
    if (value < 128n) return new Uint8Array([Number(value)])

    // General case: 7 bits per byte, high bit = continuation
    const bytes: number[] = []
    let v = value

    // First, collect 7-bit groups (LSB first)
    const groups: number[] = []
    while (v > 0n) {
        groups.push(Number(v & 0x7Fn))
        v >>= 7n
        if (groups.length === 9) break
    }

    // Write MSB first with continuation bits
    for (let i = groups.length - 1; i >= 0; i--) {
        bytes.push(i > 0 ? (groups[i] | 0x80) : groups[i])
    }
    return new Uint8Array(bytes)
}

// ── Column-Level Merge (§90 §6.3.1) ────────────────────────────

/**
 * Three-way column-level merge of two modified records against a base.
 *
 * For each column:
 *   - If only one side changed → take that side's value + serial type
 *   - If same column changed by both → null (real conflict)
 *   - If both changed to identical value → take either
 *
 * Returns merged payload bytes or null on conflict.
 */
export function tryColumnMerge(
    basePayload: Uint8Array,
    localPayload: Uint8Array,
    remotePayload: Uint8Array,
): Uint8Array | null {
    const baseCols = parseRecord(basePayload)
    const localCols = parseRecord(localPayload)
    const remoteCols = parseRecord(remotePayload)

    const baseTypes = parseSerialTypes(basePayload)
    const localTypes = parseSerialTypes(localPayload)
    const remoteTypes = parseSerialTypes(remotePayload)

    // Column count must match (same schema)
    if (baseCols.length !== localCols.length || baseCols.length !== remoteCols.length) {
        return null
    }

    let anyConflict = false
    const mergedCols: Uint8Array[] = []
    const mergedTypes: bigint[] = []

    for (let i = 0; i < baseCols.length; i++) {
        const localChanged = !uint8Equal(baseCols[i], localCols[i])
        const remoteChanged = !uint8Equal(baseCols[i], remoteCols[i])

        if (!localChanged && !remoteChanged) {
            mergedCols.push(baseCols[i])
            mergedTypes.push(baseTypes[i])
        } else if (localChanged && !remoteChanged) {
            mergedCols.push(localCols[i])
            mergedTypes.push(localTypes[i])
        } else if (!localChanged && remoteChanged) {
            mergedCols.push(remoteCols[i])
            mergedTypes.push(remoteTypes[i])
        } else {
            // Both changed
            if (uint8Equal(localCols[i], remoteCols[i])) {
                mergedCols.push(localCols[i]) // convergent
                mergedTypes.push(localTypes[i])
            } else {
                anyConflict = true
                break
            }
        }
    }

    if (anyConflict) return null

    return rebuildRecord(mergedTypes, mergedCols)
}

/** Extract serial types from a record payload header. */
function parseSerialTypes(payload: Uint8Array): bigint[] {
    const [headerSize] = readVarint(payload, 0)
    const hdrEnd = Number(headerSize)
    const types: bigint[] = []
    let off = writeVarint(headerSize).length // skip past headerSize varint
    while (off < hdrEnd) {
        const [st, nextOff] = readVarint(payload, off)
        types.push(st)
        off = nextOff
    }
    return types
}

/**
 * Rebuild a SQLite record from explicit serial types and column values.
 */
function rebuildRecord(serialTypes: bigint[], columns: Uint8Array[]): Uint8Array {
    // Build serial type varints
    const typeVarints: Uint8Array[] = serialTypes.map(st => writeVarint(st))
    const typeBytes = typeVarints.reduce((n, v) => n + v.length, 0)

    // Header size includes itself — need iterative calculation
    let hdrSizeVal = typeBytes + 1 // estimate: 1-byte varint for header size
    let hdrSizeVarint = writeVarint(BigInt(hdrSizeVal))
    while (hdrSizeVarint.length + typeBytes !== hdrSizeVal) {
        hdrSizeVal = hdrSizeVarint.length + typeBytes
        hdrSizeVarint = writeVarint(BigInt(hdrSizeVal))
    }

    // Assemble record
    const dataSize = columns.reduce((n, c) => n + c.length, 0)
    const totalSize = hdrSizeVarint.length + typeBytes + dataSize
    const result = new Uint8Array(totalSize)
    let pos = 0

    // Write header size varint
    result.set(hdrSizeVarint, pos); pos += hdrSizeVarint.length

    // Write serial type varints
    for (const tv of typeVarints) {
        result.set(tv, pos); pos += tv.length
    }

    // Write column values
    for (const col of columns) {
        result.set(col, pos); pos += col.length
    }

    return result
}

// ── Index Cell Extraction (B1: §90 §6.6) ────────────────────────

/**
 * Index cell representation. Index cells have NO separate rowId —
 * the key IS the indexed value (payload contains rowId + columns).
 */
export interface IndexCell {
    payloadSize: number
    payloadHash: Uint8Array
    payload: Uint8Array
}

type PageGetter = (pgno: number) => Uint8Array | null | Promise<Uint8Array | null>

/**
 * Extract cells from an index leaf page (type 0x0A).
 *
 * Index leaf cell format:
 *   varint(nPayload) + payload[nPayload]
 *   NO childPtr (leaf), NO separate rowId (encoded in payload)
 *
 * Overflow threshold (pageSize=4096):
 *   maxLocal = (4096-12)*64/255 - 23 = 1001 bytes
 *   minLocal = (4096-12)*32/255 - 23 = 478 bytes
 */
export async function extractIndexCells(
    page: Uint8Array, pgno: number,
    getPage?: PageGetter,
    pageSize?: number,
): Promise<IndexCell[]> {
    const hdrOff = pgno === 1 ? 100 : 0
    const view = new DataView(page.buffer, page.byteOffset)

    const pageType = page[hdrOff]
    if (pageType !== 0x0A) {
        throw new Error(`extractIndexCells: not an index leaf page (type 0x${pageType.toString(16)})`)
    }

    const cellCount = view.getUint16(hdrOff + 3)
    const cellPtrStart = hdrOff + 8 // leaf: 8-byte header

    // Overflow thresholds for index pages
    const usableSize = pageSize ?? page.length
    const maxLocal = Math.floor(((usableSize - 12) * 64) / 255) - 23
    const minLocal = Math.floor(((usableSize - 12) * 32) / 255) - 23
    const ovflUsable = usableSize - 4

    const cells: IndexCell[] = []
    for (let i = 0; i < cellCount; i++) {
        const cellOff = view.getUint16(cellPtrStart + i * 2)
        const [nPayload, off2] = readVarint(page, cellOff)
        const P = Number(nPayload)

        let payload: Uint8Array

        if (P <= maxLocal || !getPage || !pageSize) {
            // No overflow, or no page getter — read what's available
            const payloadLen = Math.min(P, page.length - off2)
            payload = page.slice(off2, off2 + payloadLen)
        } else {
            // Overflow — compute local bytes and follow chain
            let localBytes = minLocal + ((P - minLocal) % ovflUsable)
            if (localBytes > maxLocal) localBytes = minLocal

            payload = new Uint8Array(P)
            // Copy local portion
            payload.set(page.subarray(off2, off2 + localBytes), 0)

            // Read overflow pointer (4 bytes after local payload)
            let ovflPgno = view.getUint32(off2 + localBytes)
            let dataOffset = localBytes

            while (ovflPgno > 0 && dataOffset < P) {
                const ovflPage = await getPage(ovflPgno)
                if (!ovflPage) throw new Error('MISSING_OVERFLOW_PAGE')
                const ovflView = new DataView(ovflPage.buffer, ovflPage.byteOffset)
                const bytes = Math.min(ovflUsable, P - dataOffset)
                payload.set(ovflPage.subarray(4, 4 + bytes), dataOffset)
                dataOffset += bytes
                ovflPgno = ovflView.getUint32(0) // next in chain
            }
        }

        const hashBuf = await crypto.subtle.digest('SHA-256', payload as BufferSource)

        cells.push({
            payloadSize: P,
            payloadHash: new Uint8Array(hashBuf),
            payload,
        })
    }
    return cells
}

/**
 * Extract divider key entries from an index INTERIOR page as IndexCells.
 *
 * In SQLite, index interior separator keys ARE counted as index entries.
 * Interior cell format: 4B childPtr + varint(payloadSize) + payload.
 */
export async function extractInteriorIndexCells(
    page: Uint8Array, pgno: number,
    getPage?: PageGetter,
    pageSize?: number,
): Promise<IndexCell[]> {
    const hdrOff = pgno === 1 ? 100 : 0
    const view = new DataView(page.buffer, page.byteOffset)

    const pageType = page[hdrOff]
    if (pageType !== 0x02) return [] // Not an index interior page

    const cellCount = view.getUint16(hdrOff + 3)
    const cellPtrStart = hdrOff + 12 // interior: 12-byte header

    // Overflow thresholds for index pages
    const usableSize = pageSize ?? page.length
    const maxLocal = Math.floor(((usableSize - 12) * 64) / 255) - 23
    const minLocal = Math.floor(((usableSize - 12) * 32) / 255) - 23
    const ovflUsable = usableSize - 4

    const cells: IndexCell[] = []
    for (let i = 0; i < cellCount; i++) {
        const cellOff = view.getUint16(cellPtrStart + i * 2)
        // Interior cell: 4B childPtr + varint(payloadSize) + payload
        const [nPayload, off2] = readVarint(page, cellOff + 4) // skip 4B child pointer
        const P = Number(nPayload)

        let payload: Uint8Array

        if (P <= maxLocal || !getPage || !pageSize) {
            const payloadLen = Math.min(P, page.length - off2)
            payload = page.slice(off2, off2 + payloadLen)
        } else {
            // Overflow — compute local bytes and follow chain
            let localBytes = minLocal + ((P - minLocal) % ovflUsable)
            if (localBytes > maxLocal) localBytes = minLocal

            payload = new Uint8Array(P)
            payload.set(page.subarray(off2, off2 + localBytes), 0)

            let ovflPgno = view.getUint32(off2 + localBytes)
            let dataOffset = localBytes

            while (ovflPgno > 0 && dataOffset < P) {
                const ovflPage = await getPage(ovflPgno)
                if (!ovflPage) throw new Error('MISSING_OVERFLOW_PAGE')
                const ovflView = new DataView(ovflPage.buffer, ovflPage.byteOffset)
                const bytes = Math.min(ovflUsable, P - dataOffset)
                payload.set(ovflPage.subarray(4, 4 + bytes), dataOffset)
                dataOffset += bytes
                ovflPgno = ovflView.getUint32(0)
            }
        }

        const hashBuf = await crypto.subtle.digest('SHA-256', payload as BufferSource)

        cells.push({
            payloadSize: P,
            payloadHash: new Uint8Array(hashBuf),
            payload,
        })
    }
    return cells
}

/**
 * Three-way set merge for index cells (B1).
 *
 * Index cells are identified by their payload hash (the full payload IS the key).
 * Merges as set operations:
 *   result = base ∪ (local \ base) ∪ (remote \ base) \ (base \ local) \ (base \ remote)
 *
 * Returns sorted by payload bytes (B-tree key order).
 */
export function mergeIndexCellSets(
    baseCells: IndexCell[],
    localCells: IndexCell[],
    remoteCells: IndexCell[],
): IndexCell[] {
    const hashKey = (c: IndexCell) => Array.from(c.payloadHash).map(b => b.toString(16).padStart(2, '0')).join('')

    const baseSet = new Map(baseCells.map(c => [hashKey(c), c]))
    const localSet = new Map(localCells.map(c => [hashKey(c), c]))
    const remoteSet = new Map(remoteCells.map(c => [hashKey(c), c]))

    const result = new Map<string, IndexCell>()

    // Start with base
    for (const [k, c] of baseSet) result.set(k, c)
    // Add local inserts
    for (const [k, c] of localSet) if (!baseSet.has(k)) result.set(k, c)
    // Add remote inserts
    for (const [k, c] of remoteSet) if (!baseSet.has(k)) result.set(k, c)
    // Remove local deletes
    for (const [k] of baseSet) if (!localSet.has(k)) result.delete(k)
    // Remove remote deletes
    for (const [k] of baseSet) if (!remoteSet.has(k)) result.delete(k)

    // Sort by payload bytes (B-tree key order)
    return [...result.values()].sort((a, b) => compareSqliteRecords(a.payload, b.payload))
}

/**
 * Type-aware SQLite record comparison for B-tree ordering.
 *
 * Mirrors sqlite3VdbeRecordCompareWithSkip() / sqlite3MemCompare() from
 * vdbeaux.c. SQLite's B-tree index sorts records by column type affinity
 * and value, NOT by raw bytes. Type ordering:
 *
 *   NULL  <  INTEGER/REAL (numeric)  <  TEXT  <  BLOB
 *
 * Within numeric: values compared numerically (cross-type INT↔REAL
 * uses float64 promotion, matching sqlite3IntFloatCompare).
 */
export function compareSqliteRecords(a: Uint8Array, b: Uint8Array): number {
    // Parse record headers
    const aInfo = parseRecordHeader(a)
    const bInfo = parseRecordHeader(b)

    const nCols = Math.min(aInfo.types.length, bInfo.types.length)

    let aDataOff = aInfo.dataStart
    let bDataOff = bInfo.dataStart

    for (let i = 0; i < nCols; i++) {
        const aType = aInfo.types[i]
        const bType = bInfo.types[i]
        const aLen = serialTypeLen(aType)
        const bLen = serialTypeLen(bType)

        const aGroup = typeGroup(aType)
        const bGroup = typeGroup(bType)

        // Compare by type group first (NULL < numeric < TEXT < BLOB)
        if (aGroup !== bGroup) {
            aDataOff += aLen
            bDataOff += bLen
            return aGroup - bGroup
        }

        // Same group — compare by value
        let rc = 0
        switch (aGroup) {
            case 0: // Both NULL
                break

            case 1: { // Both numeric (INT or REAL)
                const aVal = decodeNumeric(a, aDataOff, aType)
                const bVal = decodeNumeric(b, bDataOff, bType)
                if (aVal < bVal) rc = -1
                else if (aVal > bVal) rc = 1
                break
            }

            case 2: // Both TEXT — memcmp then length
            case 3: // Both BLOB — memcmp then length
                {
                    const cmpLen = Math.min(aLen, bLen)
                    for (let j = 0; j < cmpLen; j++) {
                        if (a[aDataOff + j] !== b[bDataOff + j]) {
                            rc = a[aDataOff + j] - b[bDataOff + j]
                            break
                        }
                    }
                    if (rc === 0) rc = aLen - bLen
                    break
                }
        }

        aDataOff += aLen
        bDataOff += bLen

        if (rc !== 0) return rc
    }

    // All compared columns equal — shorter record sorts first
    return aInfo.types.length - bInfo.types.length
}

/** Parse a record payload header: extract serial types and compute data start offset. */
function parseRecordHeader(payload: Uint8Array): { types: number[]; dataStart: number } {
    const [headerSize, firstTypeOff] = readVarint(payload, 0)
    const hdrEnd = Number(headerSize)
    const types: number[] = []
    let off = firstTypeOff
    while (off < hdrEnd && off < payload.length) {
        const [st, nextOff] = readVarint(payload, off)
        types.push(Number(st))
        off = nextOff
    }
    return { types, dataStart: hdrEnd }
}

/**
 * Get the data length in bytes for a given serial type.
 * Matches sqlite3VdbeSerialTypeLen() in vdbeaux.c.
 */
function serialTypeLen(st: number): number {
    if (st <= 0) return 0     // NULL
    if (st <= 4) return st    // int8..int32
    if (st === 5) return 6    // int48
    if (st === 6) return 8    // int64
    if (st === 7) return 8    // float64
    if (st === 8 || st === 9) return 0  // integer 0 or 1
    if (st >= 12 && st % 2 === 0) return (st - 12) / 2 // blob
    if (st >= 13 && st % 2 === 1) return (st - 13) / 2 // text
    return 0
}

/**
 * Map serial type to a type-affinity group for comparison ordering.
 * Matches sqlite3MemCompare ordering from vdbeaux.c:4575.
 *
 * 0 = NULL, 1 = numeric (INT or REAL), 2 = TEXT, 3 = BLOB
 */
function typeGroup(st: number): number {
    if (st === 0) return 0                            // NULL
    if ((st >= 1 && st <= 9) || st === 7) return 1    // INT (1-6,8,9) or REAL (7)
    if (st >= 13 && st % 2 === 1) return 2            // TEXT (odd ≥ 13)
    if (st >= 12 && st % 2 === 0) return 3            // BLOB (even ≥ 12)
    return 0 // fallback — treat as NULL for reserved types (10, 11)
}

/**
 * Decode a numeric value (INT or REAL) from record data.
 * Returns a JS number for comparison. For very large int64 values
 * this loses precision, but it's sufficient for comparison ordering.
 *
 * Mirrors vdbeRecordDecodeInt() + serialGet7() in vdbeaux.c.
 */
function decodeNumeric(data: Uint8Array, off: number, serialType: number): number {
    const view = new DataView(data.buffer, data.byteOffset)
    switch (serialType) {
        case 0: return 0  // NULL — shouldn't reach here but safe fallback
        case 1: { // int8 (signed)
            const v = view.getUint8(off)
            return v >= 0x80 ? v - 0x100 : v
        }
        case 2: return view.getInt16(off) // int16 (big-endian signed)
        case 3: { // int24 (big-endian signed)
            const b0 = data[off], b1 = data[off + 1], b2 = data[off + 2]
            const v = (b0 << 16) | (b1 << 8) | b2
            return v >= 0x800000 ? v - 0x1000000 : v
        }
        case 4: return view.getInt32(off) // int32 (big-endian signed)
        case 5: { // int48 (big-endian signed)
            const hi = view.getInt16(off)
            const lo = view.getUint32(off + 2)
            return hi * 0x100000000 + lo
        }
        case 6: { // int64 (big-endian signed) — lose precision for very large values
            const hi = view.getInt32(off)
            const lo = view.getUint32(off + 4)
            return hi * 0x100000000 + lo
        }
        case 7: return view.getFloat64(off) // float64 (big-endian IEEE 754)
        case 8: return 0 // integer constant 0
        case 9: return 1 // integer constant 1
        default: return 0
    }
}

// ── Index Leaf Page Builder (B1) ────────────────────────────────

/**
 * Build a valid index leaf page (0x0A) from a set of index cells.
 *
 * Index leaf cell: varint(nPayload) + payload  (no rowId, no childPtr)
 */
export function buildIndexLeafPage(
    cells: IndexCell[],
    pageSize: number,
    pgno: number,
): Uint8Array | null {
    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 8
    const cellPtrArrayLen = cells.length * 2
    const usableStart = hdrOff + headerLen + cellPtrArrayLen

    // Serialize cells: varint(payloadSize) + payload
    const serializedCells: Uint8Array[] = []
    let totalCellBytes = 0
    for (const cell of cells) {
        const psVarint = writeVarint(BigInt(cell.payloadSize))
        const cellLen = psVarint.length + cell.payload.length
        const cellBuf = new Uint8Array(cellLen)
        cellBuf.set(psVarint, 0)
        cellBuf.set(cell.payload, psVarint.length)
        serializedCells.push(cellBuf)
        totalCellBytes += cellLen
    }

    if (usableStart + totalCellBytes > pageSize) return null

    const page = new Uint8Array(pageSize)
    const view = new DataView(page.buffer)

    page[hdrOff] = 0x0A // index leaf
    view.setUint16(hdrOff + 1, 0) // first free block
    view.setUint16(hdrOff + 3, cells.length)

    let cellAreaStart = pageSize
    for (let i = 0; i < serializedCells.length; i++) {
        cellAreaStart -= serializedCells[i].length
        page.set(serializedCells[i], cellAreaStart)
        view.setUint16(hdrOff + headerLen + i * 2, cellAreaStart)
    }

    view.setUint16(hdrOff + 5, cellAreaStart)
    page[hdrOff + 7] = 0

    return page
}

function indexLeafOverflowBounds(pageSize: number): { maxLocal: number; minLocal: number; overflowUsable: number } {
    return {
        maxLocal: Math.floor(((pageSize - 12) * 64) / 255) - 23,
        minLocal: Math.floor(((pageSize - 12) * 32) / 255) - 23,
        overflowUsable: pageSize - 4,
    }
}

function indexLocalPayloadBytes(payloadSize: number, pageSize: number): number {
    const { maxLocal, minLocal, overflowUsable } = indexLeafOverflowBounds(pageSize)
    if (payloadSize <= maxLocal) return payloadSize
    let localBytes = minLocal + ((payloadSize - minLocal) % overflowUsable)
    if (localBytes > maxLocal) localBytes = minLocal
    return localBytes
}

function serializeIndexLeafCellWithOverflow(
    cell: IndexCell,
    pageSize: number,
    allocPage: () => number,
): { cell: Uint8Array; overflowPages: Array<{ pgno: number; page: Uint8Array }> } {
    const P = cell.payloadSize
    const psVarint = writeVarint(BigInt(P))
    const { maxLocal, overflowUsable } = indexLeafOverflowBounds(pageSize)

    if (P <= maxLocal) {
        const cellBuf = new Uint8Array(psVarint.length + cell.payload.length)
        cellBuf.set(psVarint, 0)
        cellBuf.set(cell.payload, psVarint.length)
        return { cell: cellBuf, overflowPages: [] }
    }

    const localBytes = indexLocalPayloadBytes(P, pageSize)
    const cellBuf = new Uint8Array(psVarint.length + localBytes + 4)
    cellBuf.set(psVarint, 0)
    cellBuf.set(cell.payload.subarray(0, localBytes), psVarint.length)

    let remaining = P - localBytes
    let dataOffset = localBytes
    let firstOverflowPgno = 0
    const overflowPages: Array<{ pgno: number; page: Uint8Array }> = []
    while (remaining > 0) {
        const ovflPgno = allocPage()
        if (overflowPages.length === 0) firstOverflowPgno = ovflPgno
        const ovflPage = new Uint8Array(pageSize)
        const bytes = Math.min(overflowUsable, remaining)
        ovflPage.set(cell.payload.subarray(dataOffset, dataOffset + bytes), 4)
        dataOffset += bytes
        remaining -= bytes
        overflowPages.push({ pgno: ovflPgno, page: ovflPage })
    }

    for (let i = 0; i < overflowPages.length - 1; i += 1) {
        new DataView(overflowPages[i].page.buffer, overflowPages[i].page.byteOffset)
            .setUint32(0, overflowPages[i + 1].pgno)
    }
    new DataView(cellBuf.buffer, cellBuf.byteOffset).setUint32(psVarint.length + localBytes, firstOverflowPgno)

    return { cell: cellBuf, overflowPages }
}

function packLeafGroupsByOnPageSize(
    onPageSizes: readonly number[],
    pageSize: number,
    headerLen: number,
): number[][] {
    const groups: number[][] = []
    let current: number[] = []
    let currentBytes = 0
    const hardCapacity = pageSize - headerLen

    for (let index = 0; index < onPageSizes.length; index += 1) {
        const cellBytes = onPageSizes[index] + 2
        if (cellBytes > hardCapacity) {
            throw new RangeError(
                `sqlite3mt btree split cannot fit cell ${index} (${cellBytes} bytes) in a ${pageSize}-byte leaf page`
            )
        }
        if (current.length > 0 && currentBytes + cellBytes > hardCapacity) {
            groups.push(current)
            current = []
            currentBytes = 0
        }
        current.push(index)
        currentBytes += cellBytes
    }

    if (current.length > 0) groups.push(current)
    return groups
}

export interface IndexOverflowBuildResult {
    leafPage: Uint8Array
    overflowPages: Array<{ pgno: number; page: Uint8Array }>
    newPageCount: number
}

export function buildIndexLeafPageWithOverflow(
    cells: IndexCell[],
    pageSize: number,
    pgno: number,
    currentPageCount: number,
): IndexOverflowBuildResult | null {
    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 8
    const usableStart = hdrOff + headerLen + cells.length * 2
    let newPageCount = currentPageCount
    const allocPage = (): number => ++newPageCount

    const serializedCells: Uint8Array[] = []
    const allOverflowPages: Array<{ pgno: number; page: Uint8Array }> = []
    let totalCellBytes = 0
    for (const cell of cells) {
        const serialized = serializeIndexLeafCellWithOverflow(cell, pageSize, allocPage)
        serializedCells.push(serialized.cell)
        allOverflowPages.push(...serialized.overflowPages)
        totalCellBytes += serialized.cell.length
    }

    if (usableStart + totalCellBytes > pageSize) return null

    const page = new Uint8Array(pageSize)
    const view = new DataView(page.buffer)
    page[hdrOff] = 0x0A
    view.setUint16(hdrOff + 1, 0)
    view.setUint16(hdrOff + 3, cells.length)

    let cellAreaStart = pageSize
    for (let i = 0; i < serializedCells.length; i += 1) {
        cellAreaStart -= serializedCells[i].length
        page.set(serializedCells[i], cellAreaStart)
        view.setUint16(hdrOff + headerLen + i * 2, cellAreaStart)
    }

    view.setUint16(hdrOff + 5, cellAreaStart)
    page[hdrOff + 7] = 0

    return { leafPage: page, overflowPages: allOverflowPages, newPageCount }
}

export function splitIndexPagesWithOverflow(
    cells: IndexCell[],
    pageSize: number,
    rootPgno: number,
    currentPageCount: number,
    freePages: number[] = [],
): {
    interiorPage: { pgno: number; page: Uint8Array }
    interiorPages: Array<{ pgno: number; page: Uint8Array }>
    leafPages: Array<{ pgno: number; page: Uint8Array }>
    overflowPages: Array<{ pgno: number; page: Uint8Array }>
    newPageCount: number
} {
    const headerLen = 8
    const onPageSizes = cells.map(cell => writeVarint(BigInt(cell.payloadSize)).length
        + indexLocalPayloadBytes(cell.payloadSize, pageSize)
        + (cell.payloadSize > indexLeafOverflowBounds(pageSize).maxLocal ? 4 : 0))
    const pageGroups = packLeafGroupsByOnPageSize(onPageSizes, pageSize, headerLen)
    if (pageGroups.length < 2 && pageGroups[0]?.length > 1) {
        const group = pageGroups[0]
        const mid = Math.ceil(group.length / 2)
        pageGroups.length = 0
        pageGroups.push(group.slice(0, mid), group.slice(mid))
    }

    let newPageCount = currentPageCount
    const freeQ = [...new Set(freePages.filter(pgno => pgno > 1 && pgno !== rootPgno))].sort((a, b) => a - b)
    const allocPage = (): number => {
        if (freeQ.length > 0) return freeQ.shift()!
        return ++newPageCount
    }

    const leafPages: Array<{ pgno: number; page: Uint8Array }> = []
    const interiorPages: Array<{ pgno: number; page: Uint8Array }> = []
    const allOverflowPages: Array<{ pgno: number; page: Uint8Array }> = []
    let children: IndexInteriorChild[] = []

    for (let groupIndex = 0; groupIndex < pageGroups.length; groupIndex += 1) {
        const group = pageGroups[groupIndex]
        const leafCellIndexes = groupIndex < pageGroups.length - 1 ? group.slice(0, -1) : group
        const leafCells = leafCellIndexes.map(index => cells[index])
        const leafPgno = allocPage()

        const serializedCells: Uint8Array[] = []
        for (const cell of leafCells) {
            const serialized = serializeIndexLeafCellWithOverflow(cell, pageSize, allocPage)
            serializedCells.push(serialized.cell)
            allOverflowPages.push(...serialized.overflowPages)
        }

        const page = new Uint8Array(pageSize)
        const view = new DataView(page.buffer)
        page[0] = 0x0A
        view.setUint16(1, 0)
        view.setUint16(3, leafCells.length)

        let cellAreaStart = pageSize
        for (let i = 0; i < serializedCells.length; i += 1) {
            cellAreaStart -= serializedCells[i].length
            page.set(serializedCells[i], cellAreaStart)
            view.setUint16(headerLen + i * 2, cellAreaStart)
        }

        view.setUint16(5, cellAreaStart)
        page[7] = 0
        leafPages.push({ pgno: leafPgno, page })
        children.push({ childPgno: leafPgno, maxPayload: cells[group[group.length - 1]].payload })
    }

    let interiorPage: Uint8Array | null = null
    while (interiorPage == null) {
        const rootInterior = buildIndexInteriorPageFromChildren(children, pageSize, rootPgno, allocPage)
        if (rootInterior != null) {
            interiorPage = rootInterior.page
            allOverflowPages.push(...rootInterior.overflowPages)
            break
        }

        const nextLevel: IndexInteriorChild[] = []
        for (const group of splitIndexInteriorChildren(children, pageSize)) {
            const interiorPgno = allocPage()
            const built = buildIndexInteriorPageFromChildren(group, pageSize, interiorPgno, allocPage)
            if (built == null) throw new Error('SQLITE3MT_INDEX_INTERIOR_SPLIT_OVERFLOW')
            interiorPages.push({ pgno: interiorPgno, page: built.page })
            allOverflowPages.push(...built.overflowPages)
            nextLevel.push({ childPgno: interiorPgno, maxPayload: group[group.length - 1].maxPayload })
        }
        children = nextLevel
    }

    return {
        interiorPage: { pgno: rootPgno, page: interiorPage },
        interiorPages,
        leafPages,
        overflowPages: allOverflowPages,
        newPageCount,
    }
}

interface IndexInteriorChild {
    childPgno: number
    maxPayload: Uint8Array
}

function indexInteriorCellOnPageSize(payloadSize: number, pageSize: number): number {
    return 4
        + writeVarint(BigInt(payloadSize)).length
        + indexLocalPayloadBytes(payloadSize, pageSize)
        + (payloadSize > indexLeafOverflowBounds(pageSize).maxLocal ? 4 : 0)
}

function indexInteriorChildrenFit(children: readonly IndexInteriorChild[], pageSize: number, pgno: number): boolean {
    if (children.length < 2) return false
    const hdrOff = pgno === 1 ? 100 : 0
    const cellChildren = children.slice(0, -1)
    const bytes = cellChildren.reduce(
        (sum, child) => sum + indexInteriorCellOnPageSize(child.maxPayload.length, pageSize),
        0,
    )
    return hdrOff + 12 + cellChildren.length * 2 + bytes <= pageSize
}

function splitIndexInteriorChildren(children: readonly IndexInteriorChild[], pageSize: number): IndexInteriorChild[][] {
    const groups: IndexInteriorChild[][] = []
    let current: IndexInteriorChild[] = []
    for (const child of children) {
        const candidate = [...current, child]
        if (current.length > 0 && !indexInteriorChildrenFit(candidate, pageSize, 2)) {
            groups.push(current)
            current = [child]
        } else {
            current = candidate
        }
    }
    if (current.length > 0) groups.push(current)

    for (let index = 0; index < groups.length; index += 1) {
        if (groups[index].length !== 1) continue

        if (index > 0) {
            const previous = groups[index - 1]
            const combined = [...previous, ...groups[index]]
            if (indexInteriorChildrenFit(combined, pageSize, 2)) {
                groups.splice(index - 1, 2, combined)
                index = Math.max(0, index - 2)
                continue
            }

            if (previous.length > 2) {
                groups[index].unshift(previous.pop()!)
                if (indexInteriorChildrenFit(groups[index], pageSize, 2)) {
                    index = Math.max(0, index - 2)
                    continue
                }
            }
        }

        if (index + 1 < groups.length) {
            const next = groups[index + 1]
            if (next.length > 2) {
                groups[index].push(next.shift()!)
                if (indexInteriorChildrenFit(groups[index], pageSize, 2)) {
                    index = Math.max(0, index - 1)
                    continue
                }
            }
        }

        throw new Error('SQLITE3MT_INDEX_INTERIOR_SPLIT_SINGLETON')
    }

    return groups
}

function buildIndexInteriorPageFromChildren(
    children: readonly IndexInteriorChild[],
    pageSize: number,
    pgno: number,
    allocPage: () => number,
): { page: Uint8Array; overflowPages: Array<{ pgno: number; page: Uint8Array }> } | null {
    if (!indexInteriorChildrenFit(children, pageSize, pgno)) return null

    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 12
    const { maxLocal, minLocal, overflowUsable } = indexLeafOverflowBounds(pageSize)
    const serializedCells: Uint8Array[] = []
    const overflowPages: Array<{ pgno: number; page: Uint8Array }> = []

    for (const child of children.slice(0, -1)) {
        const payload = child.maxPayload
        const payloadSize = payload.length
        const payloadSizeVarint = writeVarint(BigInt(payloadSize))
        if (payloadSize <= maxLocal) {
            const cell = new Uint8Array(4 + payloadSizeVarint.length + payloadSize)
            const view = new DataView(cell.buffer)
            view.setUint32(0, child.childPgno)
            cell.set(payloadSizeVarint, 4)
            cell.set(payload, 4 + payloadSizeVarint.length)
            serializedCells.push(cell)
            continue
        }

        let localBytes = minLocal + ((payloadSize - minLocal) % overflowUsable)
        if (localBytes > maxLocal) localBytes = minLocal
        const cell = new Uint8Array(4 + payloadSizeVarint.length + localBytes + 4)
        const view = new DataView(cell.buffer)
        view.setUint32(0, child.childPgno)
        cell.set(payloadSizeVarint, 4)
        cell.set(payload.subarray(0, localBytes), 4 + payloadSizeVarint.length)

        let remaining = payloadSize - localBytes
        let dataOffset = localBytes
        let firstOverflowPgno = 0
        const chain: Array<{ pgno: number; page: Uint8Array }> = []
        while (remaining > 0) {
            const overflowPgno = allocPage()
            if (chain.length === 0) firstOverflowPgno = overflowPgno
            const page = new Uint8Array(pageSize)
            const bytes = Math.min(overflowUsable, remaining)
            page.set(payload.subarray(dataOffset, dataOffset + bytes), 4)
            dataOffset += bytes
            remaining -= bytes
            chain.push({ pgno: overflowPgno, page })
        }
        for (let index = 0; index < chain.length - 1; index += 1) {
            new DataView(chain[index].page.buffer, chain[index].page.byteOffset)
                .setUint32(0, chain[index + 1].pgno)
        }
        view.setUint32(4 + payloadSizeVarint.length + localBytes, firstOverflowPgno)
        serializedCells.push(cell)
        overflowPages.push(...chain)
    }

    const page = new Uint8Array(pageSize)
    const view = new DataView(page.buffer)
    page[hdrOff] = 0x02
    view.setUint16(hdrOff + 1, 0)
    view.setUint16(hdrOff + 3, serializedCells.length)
    view.setUint32(hdrOff + 8, children[children.length - 1].childPgno)

    let cellAreaStart = pageSize
    for (let index = 0; index < serializedCells.length; index += 1) {
        cellAreaStart -= serializedCells[index].length
        page.set(serializedCells[index], cellAreaStart)
        view.setUint16(hdrOff + headerLen + index * 2, cellAreaStart)
    }
    view.setUint16(hdrOff + 5, cellAreaStart)
    page[hdrOff + 7] = 0
    return { page, overflowPages }
}

// ── Page Splitting (B2: §90 §6.6) ──────────────────────────────

/**
 * Split result: multiple leaf pages + one interior page.
 */
export interface SplitResult {
    /** Leaf pages (each with their assigned page number) */
    leafPages: Array<{ pgno: number; page: Uint8Array }>
    /** Interior page that points to all leaves */
    interiorPage: { pgno: number; page: Uint8Array }
    /** Non-root interior pages when the tree needs more than one interior level */
    interiorPages: Array<{ pgno: number; page: Uint8Array }>
    /** New DB page count after allocation */
    newPageCount: number
}

function serializeLeafCell(cell: BTreeCell | IndexCell, pageType: 'table' | 'index'): Uint8Array {
    if (pageType === 'table') {
        const tc = cell as BTreeCell
        const psVarint = writeVarint(BigInt(tc.payloadSize))
        const ridVarint = writeVarint(tc.rowId)
        const buf = new Uint8Array(psVarint.length + ridVarint.length + tc.payload.length)
        buf.set(psVarint, 0)
        buf.set(ridVarint, psVarint.length)
        buf.set(tc.payload, psVarint.length + ridVarint.length)
        return buf
    }

    const ic = cell as IndexCell
    const psVarint = writeVarint(BigInt(ic.payloadSize))
    const buf = new Uint8Array(psVarint.length + ic.payload.length)
    buf.set(psVarint, 0)
    buf.set(ic.payload, psVarint.length)
    return buf
}

function packLeafGroupsByCapacity(
    serialized: readonly Uint8Array[],
    pageSize: number,
    headerLen: number,
): number[][] {
    const groups: number[][] = []
    let current: number[] = []
    let currentBytes = 0
    const hardCapacity = pageSize - headerLen

    for (let index = 0; index < serialized.length; index += 1) {
        const cellBytes = serialized[index].length + 2
        if (cellBytes > hardCapacity) {
            throw new RangeError(
                `sqlite3mt btree split cannot fit cell ${index} (${cellBytes} bytes) in a ${pageSize}-byte leaf page`
            )
        }

        if (current.length > 0 && currentBytes + cellBytes > hardCapacity) {
            groups.push(current)
            current = []
            currentBytes = 0
        }

        current.push(index)
        currentBytes += cellBytes
    }

    if (current.length > 0) {
        groups.push(current)
    }

    return groups
}

interface TableInteriorChild {
    childPgno: number
    maxRowId: bigint
}

function maxRowIdForCells(cells: readonly BTreeCell[]): bigint {
    return cells.reduce((max, cell) => cell.rowId > max ? cell.rowId : max, 0n)
}

function tableInteriorChildrenFit(children: readonly TableInteriorChild[], pageSize: number, pgno: number): boolean {
    if (children.length < 2) return true
    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 12
    const cellChildren = children.slice(0, -1)
    const pointerBytes = cellChildren.length * 2
    const cellBytes = cellChildren.reduce((sum, child) => sum + 4 + writeVarint(child.maxRowId).length, 0)
    return hdrOff + headerLen + pointerBytes + cellBytes <= pageSize
}

function packTableInteriorChildren(children: readonly TableInteriorChild[], pageSize: number): TableInteriorChild[][] {
    const groups: TableInteriorChild[][] = []
    let current: TableInteriorChild[] = []
    for (const child of children) {
        const candidate = [...current, child]
        if (current.length >= 2 && !tableInteriorChildrenFit(candidate, pageSize, 2)) {
            groups.push(current)
            current = [child]
        } else {
            current = candidate
        }
    }
    if (current.length > 0) groups.push(current)

    const last = groups[groups.length - 1]
    const previous = groups[groups.length - 2]
    if (last?.length === 1 && previous && previous.length > 2) {
        last.unshift(previous.pop()!)
    }
    return groups
}

function buildTableInteriorPageFromChildren(
    children: readonly TableInteriorChild[],
    pageSize: number,
    pgno: number,
): Uint8Array {
    if (children.length < 2) {
        throw new Error('SQLITE3MT_TABLE_INTERIOR_REQUIRES_TWO_CHILDREN')
    }
    if (!tableInteriorChildrenFit(children, pageSize, pgno)) {
        throw new Error('SQLITE3MT_TABLE_INTERIOR_PAGE_OVERFLOW')
    }

    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 12
    const rightmostChild = children[children.length - 1]
    const cellChildren = children.slice(0, -1)
    const page = new Uint8Array(pageSize)
    const view = new DataView(page.buffer)

    page[hdrOff] = 0x05
    view.setUint16(hdrOff + 1, 0)
    view.setUint16(hdrOff + 3, cellChildren.length)
    view.setUint32(hdrOff + 8, rightmostChild.childPgno)

    let cellAreaStart = pageSize
    for (let index = 0; index < cellChildren.length; index += 1) {
        const child = cellChildren[index]
        const rowId = writeVarint(child.maxRowId)
        const cell = new Uint8Array(4 + rowId.length)
        new DataView(cell.buffer).setUint32(0, child.childPgno)
        cell.set(rowId, 4)
        cellAreaStart -= cell.length
        page.set(cell, cellAreaStart)
        view.setUint16(hdrOff + headerLen + index * 2, cellAreaStart)
    }

    view.setUint16(hdrOff + 5, cellAreaStart)
    page[hdrOff + 7] = 0
    return page
}

/**
 * Split cells across the minimum number of leaf pages that can hold the
 * serialized cells without overflowing SQLite's cell pointer/content layout.
 *
 * This is deliberately size-driven rather than count-driven: B-tree cells may
 * have highly skewed payload sizes, and a leaf is valid only if its pointer
 * array and serialized cell area both fit inside the target page.
 *
 * @param cells         Cells to distribute
 * @param pageSize      Target page size
 * @param pageType      'table' (0x0D) or 'index' (0x0A)
 * @param currentPageCount  Current DB page count
 * @param freePages     Available freelist pages (B2: check before allocating)
 * @returns SplitResult with leaf pages + interior page
 */
export function splitPages(
    cells: Array<BTreeCell | IndexCell>,
    pageSize: number,
    pageType: 'table' | 'index',
    currentPageCount: number,
    freePages: number[] = [],
    rootPgno?: number,  // Optional: place interior at this pgno instead of allocating new
): SplitResult {
    const headerLen = 8  // leaf headers are 8 bytes for both types

    // Serialize all cells once, then pack leaves by actual byte capacity.
    const serialized: Uint8Array[] = cells.map(cell => serializeLeafCell(cell, pageType))
    const pageGroups = packLeafGroupsByCapacity(serialized, pageSize, headerLen)

    // If the caller reached splitPages, preserve the historical invariant that
    // the result has an interior root. Single-page rebuilds should have returned
    // through buildLeafPage/buildIndexLeafPage before this point.
    if (pageGroups.length < 2 && pageGroups[0]?.length > 1) {
        const group = pageGroups[0]
        const mid = Math.ceil(group.length / 2)
        pageGroups.length = 0
        pageGroups.push(group.slice(0, mid), group.slice(mid))
    }

    // Allocate page numbers — use freelist first (B2 optimization), then new pages
    let newPageCount = currentPageCount
    const freeQ = [...freePages]
    const allocPage = (): number => {
        if (freeQ.length > 0) return freeQ.shift()!
        return ++newPageCount
    }

    const leafPages: SplitResult['leafPages'] = []
    for (const group of pageGroups) {
        const sliceCells = group.map(index => cells[index])
        if (sliceCells.length === 0) continue

        const leafPgno = allocPage()
        const sliceSerialized = group.map(index => serialized[index])

        const page = new Uint8Array(pageSize)
        const view = new DataView(page.buffer)
        const hdrOff = 0 // split pages are never page 1

        page[hdrOff] = pageType === 'table' ? 0x0D : 0x0A
        view.setUint16(hdrOff + 1, 0)
        view.setUint16(hdrOff + 3, sliceCells.length)

        let cellAreaStart = pageSize
        for (let j = 0; j < sliceSerialized.length; j++) {
            cellAreaStart -= sliceSerialized[j].length
            page.set(sliceSerialized[j], cellAreaStart)
            view.setUint16(hdrOff + headerLen + j * 2, cellAreaStart)
        }

        view.setUint16(hdrOff + 5, cellAreaStart)
        page[hdrOff + 7] = 0

        leafPages.push({ pgno: leafPgno, page })
    }

    // Build interior page pointing to all leaves (B4)
    // Use provided rootPgno if available, otherwise allocate new page
    const interiorPgno = rootPgno ?? allocPage()
    if (pageType === 'table') {
        let children: TableInteriorChild[] = leafPages.map((lp, index) => ({
            childPgno: lp.pgno,
            maxRowId: maxRowIdForCells(pageGroups[index].map(cellIndex => cells[cellIndex] as BTreeCell)),
        }))
        const interiorPages: Array<{ pgno: number; page: Uint8Array }> = []

        while (!tableInteriorChildrenFit(children, pageSize, interiorPgno)) {
            const nextLevel: TableInteriorChild[] = []
            for (const group of packTableInteriorChildren(children, pageSize)) {
                const pagePgno = allocPage()
                interiorPages.push({
                    pgno: pagePgno,
                    page: buildTableInteriorPageFromChildren(group, pageSize, pagePgno),
                })
                nextLevel.push({
                    childPgno: pagePgno,
                    maxRowId: group[group.length - 1].maxRowId,
                })
            }
            children = nextLevel
        }

        return {
            leafPages,
            interiorPage: {
                pgno: interiorPgno,
                page: buildTableInteriorPageFromChildren(children, pageSize, interiorPgno),
            },
            interiorPages,
            newPageCount,
        }
    }

    const interiorType: 'table' | 'index' = pageType
    const interiorResult = buildInteriorPage(
        leafPages.map((lp, index) => ({
            childPgno: lp.pgno,
            cells: pageGroups[index].map(cellIndex => cells[cellIndex]),
        })),
        pageSize,
        interiorPgno,
        interiorType,
    )
    // Table/index interior without currentPageCount — always Uint8Array
    const interiorPage = interiorResult instanceof Uint8Array ? interiorResult : interiorResult.page

    // For INDEX B-trees: the divider key in the interior is also an entry.
    // SQLite counts entries in BOTH leaf and interior pages during integrity_check.
    // To avoid double-counting, the last cell of each non-rightmost child must be
    // REMOVED from the leaf (it was promoted to the interior as a separator).
    if (pageType === 'index' && leafPages.length > 1) {
        for (let i = 0; i < leafPages.length - 1; i++) {
            // Remove the last cell from this leaf (it's now in the interior)
            const group = pageGroups[i]
            const leafCellIndexes = group.slice(0, -1)
            const leafCells = leafCellIndexes.map(index => cells[index])
            const leafSerialized = leafCellIndexes.map(index => serialized[index])

            const page = new Uint8Array(pageSize)
            const view = new DataView(page.buffer)
            const hdrOff = 0

            page[hdrOff] = 0x0A // index leaf
            view.setUint16(hdrOff + 1, 0)
            view.setUint16(hdrOff + 3, leafCells.length)

            let cellAreaStart = pageSize
            for (let j = 0; j < leafSerialized.length; j++) {
                cellAreaStart -= leafSerialized[j].length
                page.set(leafSerialized[j], cellAreaStart)
                view.setUint16(hdrOff + headerLen + j * 2, cellAreaStart)
            }

            view.setUint16(hdrOff + 5, cellAreaStart)
            page[hdrOff + 7] = 0

            leafPages[i] = { pgno: leafPages[i].pgno, page }
        }
    }

    return { leafPages, interiorPage: { pgno: interiorPgno, page: interiorPage }, interiorPages: [], newPageCount }
}

// ── Interior Page Builder (B4: §90 §6.6) ────────────────────────

/**
 * Build an interior page (0x05 for table, 0x02 for index).
 *
 * Interior page header (12 bytes):
 *   [0]      page type (0x05 or 0x02)
 *   [1..2]   first free block = 0
 *   [3..4]   cell count
 *   [5..6]   cell content area start
 *   [7]      fragmented free bytes = 0
 *   [8..11]  rightmost child page number
 *
 * Table interior cell: 4B childPtr + varint(maxRowId of left child)
 * Index interior cell: 4B childPtr + varint(nPayload) + divider_key
 */
export function buildInteriorPage(
    children: Array<{
        childPgno: number
        cells: Array<BTreeCell | IndexCell>
    }>,
    pageSize: number,
    pgno: number,
    pageType: 'table' | 'index',
    currentPageCount?: number,
): Uint8Array | { page: Uint8Array; overflowPages: Array<{ pgno: number; page: Uint8Array }>; newPageCount: number } {
    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 12 // interior: 12-byte header (includes rightmost ptr)

    // Rightmost child is the LAST child (stored in header, not as a cell)
    const rightmostChild = children[children.length - 1]
    // Cells reference all children EXCEPT the rightmost
    const cellChildren = children.slice(0, -1)

    const usableSize = pageSize
    // Index interior uses different maxLocal than table leaf:
    // maxLocal = (usableSize - 12) * 64 / 255 - 23
    const indexMaxLocal = Math.floor(((usableSize - 12) * 64) / 255) - 23
    const indexMinLocal = Math.floor(((usableSize - 12) * 32) / 255) - 23
    const overflowUsable = usableSize - 4

    let newPageCount = currentPageCount ?? 0
    const allocPage = (): number => ++newPageCount
    const allOverflowPages: Array<{ pgno: number; page: Uint8Array }> = []

    // Serialize interior cells
    const serializedCells: Uint8Array[] = []
    for (const child of cellChildren) {
        if (pageType === 'table') {
            const maxRowId = child.cells.reduce(
                (max, c) => (c as BTreeCell).rowId > max ? (c as BTreeCell).rowId : max,
                0n,
            )
            const ridVarint = writeVarint(maxRowId)
            const cellBuf = new Uint8Array(4 + ridVarint.length)
            const cellView = new DataView(cellBuf.buffer)
            cellView.setUint32(0, child.childPgno)
            cellBuf.set(ridVarint, 4)
            serializedCells.push(cellBuf)
        } else {
            // Index interior: childPtr + varint(nPayload) + local_portion [+ overflow_ptr]
            const lastCell = child.cells[child.cells.length - 1] as IndexCell
            const divider = lastCell.payload
            const P = divider.length
            const psVarint = writeVarint(BigInt(P))

            if (P <= indexMaxLocal) {
                // No overflow — fits entirely on page
                const cellBuf = new Uint8Array(4 + psVarint.length + P)
                const cellView = new DataView(cellBuf.buffer)
                cellView.setUint32(0, child.childPgno)
                cellBuf.set(psVarint, 4)
                cellBuf.set(divider, 4 + psVarint.length)
                serializedCells.push(cellBuf)
            } else if (currentPageCount !== undefined) {
                // Overflow — compute local bytes using index overflow rules
                let localBytes = indexMinLocal + ((P - indexMinLocal) % overflowUsable)
                if (localBytes > indexMaxLocal) localBytes = indexMinLocal

                const cellLen = 4 + psVarint.length + localBytes + 4
                const cellBuf = new Uint8Array(cellLen)
                const cellView = new DataView(cellBuf.buffer)
                cellView.setUint32(0, child.childPgno)
                cellBuf.set(psVarint, 4)
                cellBuf.set(divider.subarray(0, localBytes), 4 + psVarint.length)

                // Build overflow chain
                let remaining = P - localBytes
                let dataOffset = localBytes
                let firstOvflPgno = 0
                const chain: Array<{ pgno: number; page: Uint8Array }> = []

                while (remaining > 0) {
                    const ovflPgno = allocPage()
                    if (chain.length === 0) firstOvflPgno = ovflPgno
                    const ovflPage = new Uint8Array(pageSize)
                    const bytes = Math.min(overflowUsable, remaining)
                    ovflPage.set(divider.subarray(dataOffset, dataOffset + bytes), 4)
                    dataOffset += bytes
                    remaining -= bytes
                    chain.push({ pgno: ovflPgno, page: ovflPage })
                }

                // Link chain
                for (let j = 0; j < chain.length - 1; j++) {
                    new DataView(chain[j].page.buffer, chain[j].page.byteOffset)
                        .setUint32(0, chain[j + 1].pgno)
                }

                // Write overflow ptr
                new DataView(cellBuf.buffer).setUint32(4 + psVarint.length + localBytes, firstOvflPgno)

                serializedCells.push(cellBuf)
                allOverflowPages.push(...chain)
            } else {
                // No currentPageCount provided — can't allocate overflow pages
                // Store full key inline (may overflow page if called without count)
                const cellBuf = new Uint8Array(4 + psVarint.length + P)
                const cellView = new DataView(cellBuf.buffer)
                cellView.setUint32(0, child.childPgno)
                cellBuf.set(psVarint, 4)
                cellBuf.set(divider, 4 + psVarint.length)
                serializedCells.push(cellBuf)
            }
        }
    }

    const page = new Uint8Array(pageSize)
    if (pgno === 1) {
        // Should not normally happen for split interior, but handle it
    }

    const view = new DataView(page.buffer)

    // Write header
    page[hdrOff] = pageType === 'table' ? 0x05 : 0x02
    view.setUint16(hdrOff + 1, 0) // first free block
    view.setUint16(hdrOff + 3, cellChildren.length) // cell count
    view.setUint32(hdrOff + 8, rightmostChild.childPgno) // rightmost child

    // Pack cells from end backward
    const cellPtrArrayLen = cellChildren.length * 2
    let cellAreaStart = pageSize
    for (let i = 0; i < serializedCells.length; i++) {
        cellAreaStart -= serializedCells[i].length
        page.set(serializedCells[i], cellAreaStart)
        view.setUint16(hdrOff + headerLen + i * 2, cellAreaStart)
    }

    view.setUint16(hdrOff + 5, cellAreaStart)
    page[hdrOff + 7] = 0

    // Return with overflow info if we produced overflow pages
    if (allOverflowPages.length > 0 || currentPageCount !== undefined) {
        return { page, overflowPages: allOverflowPages, newPageCount }
    }
    return page
}

// ── Leaf Page Builder (§90 §6.5 binary path) ────────────────────

/**
 * Build a valid table leaf page (0x0D) from a set of cells.
 *
 * Table leaf page format:
 *   [hdrOff+0]    0x0D (page type)
 *   [hdrOff+1..2] first free block = 0
 *   [hdrOff+3..4] cell count
 *   [hdrOff+5..6] cell content area start
 *   [hdrOff+7]    fragmented free bytes = 0
 *   [hdrOff+8..]  cell pointer array (2 bytes per cell)
 *   Cells packed from end of page backward.
 *
 * @param cells    Cells to include (with payload, payloadSize, rowId)
 * @param pageSize Target page size
 * @param pgno     Page number (1 → header offset 100, else 0)
 * @param templatePage Optional existing page to preserve DB header (page 1 only)
 * @returns New page bytes, or null if cells don't fit (needs page split)
 */
export function buildLeafPage(
    cells: BTreeCell[],
    pageSize: number,
    pgno: number,
    templatePage?: Uint8Array,
): Uint8Array | null {
    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 8 // table leaf header
    const cellPtrArrayLen = cells.length * 2
    const usableStart = hdrOff + headerLen + cellPtrArrayLen

    // Serialize each cell: varint(payloadSize) + varint(rowId) + payload
    const serializedCells: Uint8Array[] = []
    let totalCellBytes = 0
    for (const cell of cells) {
        const psVarint = writeVarint(BigInt(cell.payloadSize))
        const ridVarint = writeVarint(cell.rowId)
        const cellLen = psVarint.length + ridVarint.length + cell.payload.length
        const cellBuf = new Uint8Array(cellLen)
        let pos = 0
        cellBuf.set(psVarint, pos); pos += psVarint.length
        cellBuf.set(ridVarint, pos); pos += ridVarint.length
        cellBuf.set(cell.payload, pos)
        serializedCells.push(cellBuf)
        totalCellBytes += cellLen
    }

    // Check if cells fit on one page
    if (usableStart + totalCellBytes > pageSize) {
        return null // needs page split → fall back to conflict
    }

    // Build page
    const page = new Uint8Array(pageSize)

    // Preserve DB header for page 1
    if (pgno === 1 && templatePage) {
        page.set(templatePage.slice(0, 100), 0)
    }

    const view = new DataView(page.buffer)

    // Write B-tree header
    page[hdrOff] = 0x0D // table leaf
    view.setUint16(hdrOff + 1, 0) // first free block = 0
    view.setUint16(hdrOff + 3, cells.length) // cell count

    // Pack cells from end of page backward
    let cellAreaStart = pageSize
    for (let i = 0; i < serializedCells.length; i++) {
        cellAreaStart -= serializedCells[i].length
        page.set(serializedCells[i], cellAreaStart)
        // Write cell pointer
        view.setUint16(hdrOff + headerLen + i * 2, cellAreaStart)
    }

    view.setUint16(hdrOff + 5, cellAreaStart) // cell content area start
    page[hdrOff + 7] = 0 // fragmented free bytes = 0

    return page
}

/**
 * Overflow-aware result from buildLeafPageWithOverflow.
 */
export interface OverflowBuildResult {
    /** The leaf page */
    leafPage: Uint8Array
    /** Overflow pages: { pgno, page } */
    overflowPages: Array<{ pgno: number; page: Uint8Array }>
    /** Updated page count after allocating overflow pages */
    newPageCount: number
}

/**
 * Build a table leaf page with overflow chain support.
 *
 * For cells whose payload exceeds the local storage threshold (usableSize - 35),
 * writes the first portion on-page and creates overflow chain pages for the rest.
 *
 * SQLite overflow format (§90 §14.7):
 *   maxLocal = usableSize - 35
 *   minLocal = ((usableSize - 12) * 32 / 255) - 23
 *   If P > maxLocal:
 *     localBytes = minLocal + ((P - minLocal) % (usableSize - 4))
 *     if localBytes > maxLocal: localBytes = minLocal
 *     Then: on-page = localBytes of payload + 4-byte overflow page ptr
 *   Overflow page: [4B nextPtr] + [usableSize-4 bytes of data]
 *
 * @param cells          Cells with FULL payload (from extractCellsWithOverflow)
 * @param pageSize       Target page size
 * @param pgno           Page number for the leaf page
 * @param currentPageCount Current DB page count (for allocating overflow pages)
 * @param templatePage   Optional template for page 1 DB header
 * @returns Result with leaf page, overflow pages, and new page count
 */
export function buildLeafPageWithOverflow(
    cells: BTreeCell[],
    pageSize: number,
    pgno: number,
    currentPageCount: number,
    templatePage?: Uint8Array,
): OverflowBuildResult | null {
    const hdrOff = pgno === 1 ? 100 : 0
    const headerLen = 8
    const cellPtrArrayLen = cells.length * 2
    const usableStart = hdrOff + headerLen + cellPtrArrayLen

    const usableSize = pageSize // assuming 0 reserved bytes (standard)
    const maxLocal = usableSize - 35
    const minLocal = Math.floor(((usableSize - 12) * 32) / 255) - 23
    const overflowUsable = usableSize - 4 // payload bytes per overflow page

    let newPageCount = currentPageCount
    const allOverflowPages: Array<{ pgno: number; page: Uint8Array }> = []

    // Serialize each cell (potentially with overflow)
    const serializedCells: Uint8Array[] = []
    let totalCellBytes = 0

    for (const cell of cells) {
        const P = cell.payloadSize
        const psVarint = writeVarint(BigInt(P))
        const ridVarint = writeVarint(cell.rowId)

        if (P <= maxLocal) {
            // No overflow — standard serialization
            const cellLen = psVarint.length + ridVarint.length + cell.payload.length
            const cellBuf = new Uint8Array(cellLen)
            let pos = 0
            cellBuf.set(psVarint, pos); pos += psVarint.length
            cellBuf.set(ridVarint, pos); pos += ridVarint.length
            cellBuf.set(cell.payload, pos)
            serializedCells.push(cellBuf)
            totalCellBytes += cellLen
        } else {
            // Overflow — compute local storage bytes
            let localBytes = minLocal + ((P - minLocal) % overflowUsable)
            if (localBytes > maxLocal) localBytes = minLocal

            // On-page: psVarint + ridVarint + localBytes of payload + 4B overflow ptr
            const cellLen = psVarint.length + ridVarint.length + localBytes + 4
            const cellBuf = new Uint8Array(cellLen)
            let pos = 0
            cellBuf.set(psVarint, pos); pos += psVarint.length
            cellBuf.set(ridVarint, pos); pos += ridVarint.length
            cellBuf.set(cell.payload.subarray(0, localBytes), pos); pos += localBytes

            // Build overflow chain
            let remaining = P - localBytes
            let dataOffset = localBytes
            let firstOverflowPgno = 0
            const chainPages: Array<{ pgno: number; page: Uint8Array }> = []

            while (remaining > 0) {
                const ovflPgno = ++newPageCount
                if (chainPages.length === 0) firstOverflowPgno = ovflPgno

                const ovflPage = new Uint8Array(pageSize)
                const bytesToWrite = Math.min(overflowUsable, remaining)
                // Payload data starts at offset 4 (after next-page pointer)
                ovflPage.set(cell.payload.subarray(dataOffset, dataOffset + bytesToWrite), 4)
                dataOffset += bytesToWrite
                remaining -= bytesToWrite

                chainPages.push({ pgno: ovflPgno, page: ovflPage })
            }

            // Link overflow pages: each page's first 4 bytes = next page in chain (0 = end)
            for (let j = 0; j < chainPages.length - 1; j++) {
                const view = new DataView(chainPages[j].page.buffer, chainPages[j].page.byteOffset)
                view.setUint32(0, chainPages[j + 1].pgno)
            }
            // Last overflow page: next = 0 (already zero-initialized)

            // Write first overflow page pointer into cell
            new DataView(cellBuf.buffer, cellBuf.byteOffset).setUint32(pos, firstOverflowPgno)

            serializedCells.push(cellBuf)
            totalCellBytes += cellLen
            allOverflowPages.push(...chainPages)
        }
    }

    // Check if cells fit on one page (considering on-page portions only)
    if (usableStart + totalCellBytes > pageSize) {
        return null // needs page split — caller handles this
    }

    // Build the leaf page
    const page = new Uint8Array(pageSize)

    if (pgno === 1 && templatePage) {
        page.set(templatePage.slice(0, 100), 0)
    }

    const view = new DataView(page.buffer)
    page[hdrOff] = 0x0D // table leaf
    view.setUint16(hdrOff + 1, 0)
    view.setUint16(hdrOff + 3, cells.length)

    let cellAreaStart = pageSize
    for (let i = 0; i < serializedCells.length; i++) {
        cellAreaStart -= serializedCells[i].length
        page.set(serializedCells[i], cellAreaStart)
        view.setUint16(hdrOff + headerLen + i * 2, cellAreaStart)
    }

    view.setUint16(hdrOff + 5, cellAreaStart)
    page[hdrOff + 7] = 0

    return {
        leafPage: page,
        overflowPages: allOverflowPages,
        newPageCount,
    }
}

/**
 * Split table leaf cells across N pages with overflow chain support.
 *
 * Combines splitPages logic (distributing cells across leaf pages) with
 * buildLeafPageWithOverflow logic (writing overflow chains for large payloads).
 *
 * Algorithm:
 *   1. Compute on-page size for each cell (considering overflow portion)
 *   2. Distribute cells across leaf pages at ~75% fill based on on-page sizes
 *   3. Build each leaf page, creating overflow chains for cells > maxLocal
 *   4. Build interior page pointing to all leaves
 *
 * SQLite overflow format (verified against btree.c:1198, btree.c:3439):
 *   maxLocal = usableSize - 35
 *   minLocal = ((usableSize - 12) * 32 / 255) - 23
 *   surplus  = minLocal + ((P - minLocal) % (usableSize - 4))
 *   nLocal   = surplus <= maxLocal ? surplus : minLocal
 *
 * @param cells   Cells with FULL payload (from overflow-aware extractCells)
 * @param pageSize Target page size
 * @param rootPgno Place the interior page at this pgno (table root)
 * @param currentPageCount Current DB page count for allocating new pages
 * @param templatePage Optional page 1 template for DB header preservation
 */
export function splitPagesWithOverflow(
    cells: BTreeCell[],
    pageSize: number,
    rootPgno: number,
    currentPageCount: number,
    templatePage?: Uint8Array,
): {
    interiorPage: { pgno: number; page: Uint8Array }
    interiorPages: Array<{ pgno: number; page: Uint8Array }>
    leafPages: Array<{ pgno: number; page: Uint8Array }>
    overflowPages: Array<{ pgno: number; page: Uint8Array }>
    newPageCount: number
} {
    const usableSize = pageSize
    const maxLocal = usableSize - 35
    const minLocal = Math.floor(((usableSize - 12) * 32) / 255) - 23
    const overflowUsable = usableSize - 4

    // Step 1: Compute on-page size for each cell
    const onPageSizes: number[] = cells.map(cell => {
        const P = cell.payloadSize
        const psVarint = writeVarint(BigInt(P))
        const ridVarint = writeVarint(cell.rowId)
        if (P <= maxLocal) {
            return psVarint.length + ridVarint.length + P
        }
        // Overflow case: compute localBytes
        let localBytes = minLocal + ((P - minLocal) % overflowUsable)
        if (localBytes > maxLocal) localBytes = minLocal
        return psVarint.length + ridVarint.length + localBytes + 4 // +4 for overflow ptr
    })

    // Step 2: Distribute cells across pages at ~75% fill
    const headerLen = 8
    const usablePerPage = pageSize - headerLen
    const targetFill = usablePerPage * 0.75

    // Group cells into pages
    const pageGroups: number[][] = [[]]
    let currentGroupSize = 0
    for (let i = 0; i < cells.length; i++) {
        const cellSize = onPageSizes[i] + 2 // +2 for cell pointer
        if (currentGroupSize + cellSize > targetFill && pageGroups[pageGroups.length - 1].length > 0) {
            pageGroups.push([])
            currentGroupSize = 0
        }
        pageGroups[pageGroups.length - 1].push(i)
        currentGroupSize += cellSize
    }

    // Ensure at least 2 pages for interior node to make sense
    if (pageGroups.length < 2) {
        // Force split into 2 groups
        const allIndices = pageGroups.flat()
        const mid = Math.ceil(allIndices.length / 2)
        pageGroups.length = 0
        pageGroups.push(allIndices.slice(0, mid), allIndices.slice(mid))
    }

    // Step 3: Allocate page numbers and build each leaf page with overflow
    let newPageCount = currentPageCount
    const allocPage = (): number => ++newPageCount

    const leafPages: Array<{ pgno: number; page: Uint8Array }> = []
    const allOverflowPages: Array<{ pgno: number; page: Uint8Array }> = []

    for (const group of pageGroups) {
        const leafPgno = allocPage()
        const groupCells = group.map(i => cells[i])
        const hdrOff = 0 // split pages are never page 1
        const cellPtrLen = groupCells.length * 2

        // Serialize each cell with overflow support
        const serialized: Uint8Array[] = []
        for (const cell of groupCells) {
            const P = cell.payloadSize
            const psVarint = writeVarint(BigInt(P))
            const ridVarint = writeVarint(cell.rowId)

            if (P <= maxLocal) {
                const buf = new Uint8Array(psVarint.length + ridVarint.length + cell.payload.length)
                let pos = 0
                buf.set(psVarint, pos); pos += psVarint.length
                buf.set(ridVarint, pos); pos += ridVarint.length
                buf.set(cell.payload, pos)
                serialized.push(buf)
            } else {
                // Overflow path
                let localBytes = minLocal + ((P - minLocal) % overflowUsable)
                if (localBytes > maxLocal) localBytes = minLocal

                const cellLen = psVarint.length + ridVarint.length + localBytes + 4
                const cellBuf = new Uint8Array(cellLen)
                let pos = 0
                cellBuf.set(psVarint, pos); pos += psVarint.length
                cellBuf.set(ridVarint, pos); pos += ridVarint.length
                cellBuf.set(cell.payload.subarray(0, localBytes), pos); pos += localBytes

                // Build overflow chain
                let remaining = P - localBytes
                let dataOffset = localBytes
                let firstOvflPgno = 0
                const chain: Array<{ pgno: number; page: Uint8Array }> = []

                while (remaining > 0) {
                    const ovflPgno = allocPage()
                    if (chain.length === 0) firstOvflPgno = ovflPgno

                    const ovflPage = new Uint8Array(pageSize)
                    const bytes = Math.min(overflowUsable, remaining)
                    ovflPage.set(cell.payload.subarray(dataOffset, dataOffset + bytes), 4)
                    dataOffset += bytes
                    remaining -= bytes
                    chain.push({ pgno: ovflPgno, page: ovflPage })
                }

                // Link chain
                for (let j = 0; j < chain.length - 1; j++) {
                    new DataView(chain[j].page.buffer, chain[j].page.byteOffset)
                        .setUint32(0, chain[j + 1].pgno)
                }

                // Write overflow ptr into cell
                new DataView(cellBuf.buffer, cellBuf.byteOffset).setUint32(pos, firstOvflPgno)

                serialized.push(cellBuf)
                allOverflowPages.push(...chain)
            }
        }

        // Build leaf page
        const page = new Uint8Array(pageSize)
        const view = new DataView(page.buffer)
        page[hdrOff] = 0x0D // table leaf
        view.setUint16(hdrOff + 1, 0)
        view.setUint16(hdrOff + 3, groupCells.length)

        let cellArea = pageSize
        for (let i = 0; i < serialized.length; i++) {
            cellArea -= serialized[i].length
            page.set(serialized[i], cellArea)
            view.setUint16(hdrOff + headerLen + i * 2, cellArea)
        }

        view.setUint16(hdrOff + 5, cellArea)
        page[hdrOff + 7] = 0

        leafPages.push({ pgno: leafPgno, page })
    }

    // Step 4: Build interior page pointing to all leaves
    const interiorPgno = rootPgno
    let children: TableInteriorChild[] = leafPages.map((page, index) => ({
        childPgno: page.pgno,
        maxRowId: maxRowIdForCells(pageGroups[index].map(i => cells[i])),
    }))
    const interiorPages: Array<{ pgno: number; page: Uint8Array }> = []

    while (!tableInteriorChildrenFit(children, pageSize, interiorPgno)) {
        const nextLevel: TableInteriorChild[] = []
        for (const group of packTableInteriorChildren(children, pageSize)) {
            const pagePgno = allocPage()
            interiorPages.push({
                pgno: pagePgno,
                page: buildTableInteriorPageFromChildren(group, pageSize, pagePgno),
            })
            nextLevel.push({
                childPgno: pagePgno,
                maxRowId: group[group.length - 1].maxRowId,
            })
        }
        children = nextLevel
    }

    return {
        interiorPage: {
            pgno: interiorPgno,
            page: buildTableInteriorPageFromChildren(children, pageSize, interiorPgno),
        },
        interiorPages,
        leafPages,
        overflowPages: allOverflowPages,
        newPageCount,
    }
}
// ── Page 1 Auto-Merge (§90 §6.4) ───────────────────────────────

/**
 * Field-level merge of page 1 header (100-byte SQLite database header).
 *
 * | Offset | Field                | Merge rule                      |
 * |--------|----------------------|---------------------------------|
 * | 24     | file_change_counter  | max(local, remote) + 1          |
 * | 28     | database_size        | max(local, remote)              |
 * | 32     | first_freelist_page  | must match or → null            |
 * | 36     | freelist_page_count  | must match or → null            |
 * | 40     | schema_cookie        | must match (checked upstream)    |
 * | 44–92  | format/cache/etc     | take from merged B-tree content |
 * | 92     | version_valid_for    | max(local, remote) + 1          |
 *
 * Modifies the page in-place. Returns false if structural conflict.
 */
export function mergePage1Header(
    mergedPage: Uint8Array,
    localPage1: Uint8Array,
    remotePage1: Uint8Array,
): boolean {
    const mv = new DataView(mergedPage.buffer, mergedPage.byteOffset)
    const lv = new DataView(localPage1.buffer, localPage1.byteOffset)
    const rv = new DataView(remotePage1.buffer, remotePage1.byteOffset)

    // file_change_counter: max + 1
    const localFCC = lv.getUint32(24)
    const remoteFCC = rv.getUint32(24)
    mv.setUint32(24, Math.max(localFCC, remoteFCC) + 1)

    // database_size: max (pages might grow on either side)
    const localSize = lv.getUint32(28)
    const remoteSize = rv.getUint32(28)
    mv.setUint32(28, Math.max(localSize, remoteSize))

    // freelist: must match between local and remote, else conflict
    const localFL = lv.getUint32(32)
    const remoteFL = rv.getUint32(32)
    const localFLC = lv.getUint32(36)
    const remoteFLC = rv.getUint32(36)
    if (localFL !== remoteFL || localFLC !== remoteFLC) {
        // Structural conflict — freelist diverged
        // For now, take the larger page count's freelist
        if (localSize >= remoteSize) {
            mv.setUint32(32, localFL)
            mv.setUint32(36, localFLC)
        } else {
            mv.setUint32(32, remoteFL)
            mv.setUint32(36, remoteFLC)
        }
    }

    // version_valid_for: max + 1
    const localVVF = lv.getUint32(92)
    const remoteVVF = rv.getUint32(92)
    mv.setUint32(92, Math.max(localVVF, remoteVVF) + 1)

    return true
}

// ── sqlite_master Parser (Global Index Reconstruction) ──────────

/**
 * Entry from `sqlite_master` — indexes and tables.
 */
export interface SqliteMasterEntry {
    type: 'index' | 'table'
    name: string
    tblName: string
    rootPage: number
    sql?: string
}

/**
 * Parse `sqlite_master` table to discover index definitions.
 *
 * `sqlite_master` is always rooted at page 1 (table leaf or interior).
 * Its schema is: (type TEXT, name TEXT, tbl_name TEXT, rootpage INTEGER, sql TEXT)
 *
 * We walk the B-tree, extract all cells, parse their record payloads,
 * and return index entries with their root page numbers.
 *
 * @param getPage  Page getter: pgno → page data (zero-copy)
 * @param pageSize Database page size
 */
export async function parseSqliteMaster(
    getPage: (pgno: number) => Uint8Array | null,
    pageSize: number,
): Promise<SqliteMasterEntry[]> {
    // Collect all leaf page numbers for the sqlite_master table (rooted at page 1)
    const masterLeafPages = collectBTreeLeafPages(1, getPage, pageSize)

    const entries: SqliteMasterEntry[] = []

    for (const leafPgno of masterLeafPages) {
        const page = getPage(leafPgno)
        if (!page) continue

        const pageType = getPageType(page, leafPgno)
        if (pageType !== BTreePageType.TableLeaf) continue

        const cells = await extractCells(page, leafPgno, getPage, pageSize)
        for (const cell of cells) {
            try {
                const cols = parseRecord(cell.payload)
                // sqlite_master schema: type, name, tbl_name, rootpage, sql
                if (cols.length < 4) continue

                const type = new TextDecoder().decode(cols[0])
                if (type !== 'index' && type !== 'table') continue

                const name = new TextDecoder().decode(cols[1])
                const tblName = new TextDecoder().decode(cols[2])
                const sql = cols.length >= 5 ? new TextDecoder().decode(cols[4]) : undefined

                // rootpage is stored as integer in the record
                // Parse the integer value from the raw bytes
                let rootPage = 0
                const rpBytes = cols[3]
                if (rpBytes.length === 1) rootPage = rpBytes[0]
                else if (rpBytes.length === 2) rootPage = new DataView(rpBytes.buffer, rpBytes.byteOffset).getInt16(0)
                else if (rpBytes.length === 3) rootPage = (rpBytes[0] << 16) | (rpBytes[1] << 8) | rpBytes[2]
                else if (rpBytes.length === 4) rootPage = new DataView(rpBytes.buffer, rpBytes.byteOffset).getInt32(0)
                else if (rpBytes.length === 0) rootPage = 0

                if (rootPage <= 0 && !/^create\s+virtual\s+table\b/i.test(sql ?? '')) continue

                entries.push({ type: type as 'index' | 'table', name, tblName, rootPage, sql })
            } catch {
                // Skip malformed records
            }
        }
    }

    return entries
}

/**
 * Collect ALL leaf page numbers of a B-tree rooted at `rootPgno`.
 *
 * Uses iterative DFS (stack-based) to avoid recursion depth issues.
 * Handles both table and index B-trees (interior types 0x05 and 0x02).
 *
 * @returns Set of leaf page numbers (0x0D or 0x0A pages)
 */
export function collectBTreeLeafPages(
    rootPgno: number,
    getPage: (pgno: number) => Uint8Array | null,
    pageSize: number,
): Set<number> {
    const leafPages = new Set<number>()
    const stack: number[] = [rootPgno]
    const visited = new Set<number>()

    while (stack.length > 0) {
        const pgno = stack.pop()!
        if (visited.has(pgno)) continue
        visited.add(pgno)

        const page = getPage(pgno)
        if (!page) continue

        const type = getPageType(page, pgno)

        if (type === BTreePageType.TableLeaf || type === BTreePageType.IndexLeaf) {
            leafPages.add(pgno)
            continue
        }

        if (type === BTreePageType.TableInterior || type === BTreePageType.IndexInterior) {
            // Walk interior page: extract child page pointers
            const hdrOff = pgno === 1 ? 100 : 0
            const view = new DataView(page.buffer, page.byteOffset)
            const cellCount = view.getUint16(hdrOff + 3)
            const rightmostChild = view.getUint32(hdrOff + 8)

            // Interior header is 12 bytes; cell pointers start at hdrOff + 12
            for (let i = 0; i < cellCount; i++) {
                const cellPtrOff = hdrOff + 12 + i * 2
                const cellOff = view.getUint16(cellPtrOff)
                // Interior cell starts with 4-byte child page number
                const childPgno = view.getUint32(cellOff)
                if (childPgno > 0 && childPgno <= (pageSize > 0 ? 1_000_000 : 0)) {
                    stack.push(childPgno)
                }
            }

            // Rightmost child (stored in header, not in cell)
            if (rightmostChild > 0) {
                stack.push(rightmostChild)
            }
        }
    }

    return leafPages
}

function uint8Equal(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false
    }
    return true
}
